import type { PropertyValidationResult } from '../../../types/properties';

export default function validator(properties: Record<string, any>): PropertyValidationResult {
  const errors: Array<{ key: string; message: string }> = [];
  const action = properties.action;

  // Validate action is selected
  if (!action) {
    errors.push({
      key: 'action',
      message: 'Action is required',
    });
    return { valid: false, errors };
  }

  // Validate based on action
  switch (action) {
    case 'downloadDocument':
    case 'getDocument':
      if (!properties.documentId) {
        errors.push({
          key: 'documentId',
          message: 'Document ID is required for this action',
        });
      }
      break;

    case 'updateDocument':
      if (!properties.documentId) {
        errors.push({
          key: 'documentId',
          message: 'Document ID is required for update',
        });
      }
      // At least one field must be provided for update
      if (!properties.title && !properties.destinationFolderId &&
          !properties.docTypeId && !properties.tagValues) {
        errors.push({
          key: 'title',
          message: 'At least one field (title, destinationFolderId, docTypeId, or tagValues) must be provided for update',
        });
      }
      break;

    case 'uploadDocument':
      if (!properties.fileContent) {
        errors.push({
          key: 'fileContent',
          message: 'File content is required for upload',
        });
      }
      if (!properties.fileName) {
        errors.push({
          key: 'fileName',
          message: 'File name is required for upload',
        });
      }
      if (!properties.title) {
        errors.push({
          key: 'title',
          message: 'Title is required for upload',
        });
      }
      if (!properties.destinationFolderId) {
        errors.push({
          key: 'destinationFolderId',
          message: 'Destination folder ID is required for upload',
        });
      }
      break;

    case 'listDocuments':
      // Validate take is within bounds if provided
      if (properties.take && (properties.take < 1 || properties.take > 1000)) {
        errors.push({
          key: 'take',
          message: 'Take must be between 1 and 1000',
        });
      }
      // Validate skip is non-negative if provided
      if (properties.skip && properties.skip < 0) {
        errors.push({
          key: 'skip',
          message: 'Skip must be a non-negative number',
        });
      }
      break;

    case 'listDocumentTypes':
    case 'listFolders':
    case 'listTags':
      // No additional validation required
      break;
  }

  // Validate tagValues JSON format if provided
  if (properties.tagValues && typeof properties.tagValues === 'string' && properties.tagValues.trim()) {
    try {
      const parsed = JSON.parse(properties.tagValues);
      if (!Array.isArray(parsed)) {
        errors.push({
          key: 'tagValues',
          message: 'Tag values must be a JSON array',
        });
      } else {
        // Validate each tag has tagId and tagValue
        for (const tag of parsed) {
          if (typeof tag.tagId === 'undefined' || typeof tag.tagValue === 'undefined') {
            errors.push({
              key: 'tagValues',
              message: 'Each tag must have tagId and tagValue properties',
            });
            break;
          }
        }
      }
    } catch {
      errors.push({
        key: 'tagValues',
        message: 'Tag values must be valid JSON',
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
